import { useEffect, useMemo, useRef, useState } from 'react';
import { searchDashboardCourses } from '../services/dashboard';
import { mapCourse } from '../utils/dashboardMappers';

const SEARCH_DEBOUNCE_MS = 350;

function getFirstWord(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0] || '';
}

function matchesAllWords(course, query) {
  const tokens = String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!tokens.length) return false;

  const haystack = `${course.title || ''} ${course.description || ''} ${course.subject || ''}`.toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}

export function useHeaderSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cachedCourses, setCachedCourses] = useState([]);
  const fetchedWordRef = useRef('');
  const requestIdRef = useRef(0);

  const firstWord = useMemo(() => getFirstWord(query), [query]);

  useEffect(() => {
    if (!firstWord) {
      fetchedWordRef.current = '';
      setCachedCourses([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    // Same first word already fetched, or query only refined after that word.
    if (
      fetchedWordRef.current &&
      (firstWord === fetchedWordRef.current || firstWord.startsWith(fetchedWordRef.current))
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);

      searchDashboardCourses(firstWord)
        .then((res) => {
          if (requestId !== requestIdRef.current) return;

          const courses = (res?.data?.courses || []).map(mapCourse).filter(Boolean);
          fetchedWordRef.current = firstWord;
          setCachedCourses(courses);
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          fetchedWordRef.current = '';
          setCachedCourses([]);
          setError(e?.message || 'Search failed');
        })
        .finally(() => {
          if (requestId === requestIdRef.current) {
            setLoading(false);
          }
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [firstWord]);

  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    return cachedCourses.filter((course) => matchesAllWords(course, query)).slice(0, 8);
  }, [cachedCourses, query]);

  const clearSearch = () => {
    setQuery('');
    setOpen(false);
    setCachedCourses([]);
    setError(null);
    fetchedWordRef.current = '';
  };

  return {
    query,
    setQuery,
    open,
    setOpen,
    loading,
    error,
    suggestions,
    clearSearch,
  };
}
